import { ReactRenderer } from '@tiptap/react';
import type { SuggestionOptions, SuggestionProps } from '@tiptap/suggestion';
import type Fuse from 'fuse.js';
import tippy, { type Instance } from 'tippy.js';

import {
  MentionItem,
  MentionSuggestion,
  MentionSuggestionHandle,
} from './MentionSuggestion';

/**
 * Builds a TipTap suggestion config for @mention items.
 *
 * @param mentionItems - List of available mention items.
 * @param searchItems - Fuse instance for searching mentionItems.
 * @returns Suggestion options (without editor instance).
 */
export const createSuggestion = (
  mentionItems: MentionItem[] | undefined,
  searchItems: Fuse<MentionItem>
): Omit<SuggestionOptions<MentionItem>, 'editor'> => ({
  char: '@',
  items: ({ query }) => {
    if (!mentionItems) return [];
    if (!query) return mentionItems;

    return searchItems.search(query).map((result) => result.item);
  },
  render: () => {
    let component: ReactRenderer<
      MentionSuggestionHandle,
      { items: MentionItem[]; command: (item: MentionItem) => void }
    >;
    let popup: Instance[];

    return {
      onStart: (props: SuggestionProps<MentionItem>) => {
        component = new ReactRenderer(MentionSuggestion, {
          props,
          editor: props.editor,
        });

        if (!props.clientRect) return;

        popup = tippy('body', {
          getReferenceClientRect: props.clientRect as () => DOMRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
        });
      },

      onUpdate: (props: SuggestionProps<MentionItem>) => {
        if (!component) return;
        component.updateProps(props);

        if (!props.clientRect || !popup?.[0]) return;

        popup[0].setProps({
          getReferenceClientRect: props.clientRect as () => DOMRect,
        });
      },

      onKeyDown: (props: { event: KeyboardEvent }) => {
        if (props.event.key === 'Escape') {
          if (popup?.[0]) {
            popup[0].hide();
          }

          return true;
        }

        if (!component?.ref) return false;

        return component.ref.onKeyDown(props);
      },

      onExit: () => {
        if (popup?.[0]) {
          popup[0].destroy();
        }
        if (component) {
          component.destroy();
        }
      },
    };
  },
});
